import { useCallback } from "react";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import { getToolTipAttributes } from "../../../utils/tooltip";
import KeyboardShortcutsInfoPopover from "./KeyboardShortcutsInfoPopover";

const PositionScreenActionBar = ({ screenLayoutInfo, setScreenLayoutInfo, device }) => {
  const { showGrid, showTree, screenMode, gridSize, scaleFactor } = screenLayoutInfo;

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 1.5;
  const SCALE_STEP = 0.1;

  const handleChangeLayoutInfo = useCallback(
    (type, value) => {
      setScreenLayoutInfo((prev) => {
        const updated = { ...prev, [type]: value };
        return updated;
      });
    },
    [setScreenLayoutInfo]
  );

  const handleChangeGridSize = (e) => {
    handleChangeLayoutInfo("gridSize", parseInt(e.target.value, 10));
  };

  const handleZoomIn = () => {
    if (scaleFactor < MAX_SCALE) {
      handleChangeLayoutInfo("scaleFactor", parseFloat((scaleFactor + SCALE_STEP).toFixed(2)));
    }
  };

  const handleZoomOut = () => {
    if (scaleFactor > MIN_SCALE) {
      handleChangeLayoutInfo("scaleFactor", parseFloat((scaleFactor - SCALE_STEP).toFixed(2)));
    }
  };

  return (
    <div
      className="position_absolute left_50 translate_-x_50 d_flex gap_3 align_center shadow_md border_radius_large px_3 py_2"
      style={{
        bottom: 20,
        backgroundColor: "#ffffffcc",
        backdropFilter: "blur(10px)",
        zIndex: 10
      }}
    >
      {device === "desktop" && (
        <>
          <button
            onClick={handleZoomOut}
            title="Zoom Out"
            className="border_none border_radius_medium d_flex align_center justify_center"
            style={{
              backgroundColor: "#f3f4f6",
              width: 36,
              height: 36,
              cursor: scaleFactor > MIN_SCALE ? "pointer" : "not-allowed",
              color: scaleFactor > MIN_SCALE ? "#374151" : "#9ca3af",
              transition: "background-color 0.2s ease"
            }}
            disabled={scaleFactor <= MIN_SCALE}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.95)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <MaterialIcon name="zoom_out" />
          </button>

          <span
            className="font_weight_semibold text_center font_size_medium"
            style={{
              minWidth: 45
            }}
            title={`Current Scale: ${Math.round(scaleFactor * 100)}%`}
          >
            {Math.round(scaleFactor * 100)}%
          </span>

          <button
            className="border_none border_radius_medium d_flex align_center justify_center"
            onClick={handleZoomIn}
            title="Zoom In"
            style={{
              backgroundColor: "#f3f4f6",
              width: 36,
              height: 36,
              cursor: scaleFactor < MAX_SCALE ? "pointer" : "not-allowed",
              color: scaleFactor < MAX_SCALE ? "#374151" : "#9ca3af",
              transition: "background-color 0.2s ease"
            }}
            disabled={scaleFactor >= MAX_SCALE}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.95)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <MaterialIcon name="zoom_in" />
          </button>
        </>
      )}
      {screenMode === "editable" && (
        <>
          <div title="View Keyboard Shortcuts">
            <KeyboardShortcutsInfoPopover />
          </div>

          <div className="d_flex align_center gap_2 font_size_small">
            <label htmlFor="gridSize" style={{ fontWeight: 600 }} title="Grid Snapping Size">
              Size
            </label>
            <select
              id="gridSize"
              value={Number(gridSize)}
              onChange={handleChangeGridSize}
              className="border_full border_radius_medium cursor_pointer px_2 py_1"
              title="Adjust Grid Spacing"
            >
              {[4, 8, 16, 32, 64].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* <IconToggleButton
            iconName="dashboard_customize"
            label="Templates"
            active={showTemplate}
            onToggle={(val) => handleChangeLayoutInfo("showTemplate", val)}
            title={showTemplate ? "Hide Templates Library" : "Show Templates Library"}
          /> */}

          <IconToggleButton
            iconName="account_tree"
            label="Tree"
            active={showTree}
            onToggle={(val) => handleChangeLayoutInfo("showTree", val)}
            title={showTree ? "Hide Component Tree" : "Show Component Tree"}
          />

          <IconToggleButton
            iconName="grid_view"
            label="Grid"
            active={showGrid}
            onToggle={(val) => handleChangeLayoutInfo("showGrid", val)}
            title={showGrid ? "Hide Alignment Grid" : "Show Alignment Grid"}
          />
        </>
      )}

      {/* Edit mode toggle */}
      <IconToggleButton
        iconName="edit"
        label="Edit"
        active={screenMode === "editable"}
        onToggle={(val) => handleChangeLayoutInfo("screenMode", val ? "editable" : "view")}
        title={screenMode === "editable" ? "Switch to Preview Mode" : "Enable Edit Mode"}
      />
    </div>
  );
};

export const IconToggleButton = ({ iconName, label, active, onToggle, title }) => {
  return (
    <button
      className="d_flex align_center justify_center flex_column cursor_pointer border_none border_radius_medium"
      onClick={() => onToggle(!active)}
      {...getToolTipAttributes(title || label)}
      style={{
        width: 40,
        height: 40,
        backgroundColor: active ? "#4f46e5" : "transparent", // Indigo background if active
        color: active ? "white" : "#6b7280", // White text if active, gray if not
        transition: "all 0.25s ease"
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.95)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      aria-pressed={active}
      type="button"
    >
      <MaterialIcon name={iconName} style={{ fontSize: 22, pointerEvents: "none", userSelect: "none" }} />
    </button>
  );
};

export default PositionScreenActionBar;
